<template>
  <div class="border border-[#E5E5E5] rounded-2xl p-6 bg-white shadow-sm">
    <div class="flex items-center justify-between mb-[.75rem]">
      <h2 class="font-medium text-xl text-black mb-0 border-b border-[#F0F0F0]">
        Informasi Invoice
      </h2>
      <span
        class="badge-status-invoice"
        :class="getInvoiceBadgeClass(data.invoice.status)"
      >{{ getInvoiceLabel(data.invoice.status) }}</span>
    </div>

    <!-- Mode: Has Invoice / Menunggu Pembayaran -->
    <template v-if="hasInvoice">
      <div class="my-[1rem]">
        <div class="flex justify-end items-center gap-x-[.25rem] text-sm mb-[.5rem]">
          <span>Due date:</span>
          <span class="k-clock-1 text-primary" />
          <span class="text-primary font-medium">{{ dueDateFormatted }}</span>
        </div>
        <!-- Talent & Periode Info -->
        <div class="flex items-center justify-between mb-[1rem]">
          <div>
            <div class="text-sm text-[#737373] mb-[.25rem]">
              Nama Talent
            </div>
            <div class="font-semibold text-lg text-[#EC5A24]">
              {{ talentName }}
            </div>
          </div>
          <div class="text-right">
            <div class="text-sm text-[#737373] mb-[.25rem]">
              Periode
            </div>
            <div class="font-semibold text-lg text-[#EC5A24]">
              {{ periode }}
            </div>
          </div>
        </div>

        <!-- Nominal Info Table Box -->
        <div class="border border-[#E5E5E5] rounded-xl overflow-hidden">
          <div class="bg-[#F8F8F8] px-[1rem] py-[.75rem] border-b border-[#E5E5E5] grid grid-cols-3 gap-[1rem]">
            <span class="font-medium text-sm text-[#262626]">Biaya Admin</span>
            <span class="font-medium text-sm text-[#262626] text-center">Gaji Talent</span>
            <span class="font-medium text-sm text-[#262626] text-center">Biaya Lainnya</span>
          </div>
          <div class="bg-white px-[1rem] py-[.75rem] grid grid-cols-3 gap-[1rem]">
            <span class="text-sm text-black font-normal">{{ adminFeeFormated }}</span>
            <span class="text-sm text-black font-normal text-center">{{ talentSalaryFormatted }}</span>
            <span class="text-sm text-black font-normal text-center">{{ additionalCostFormatted }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- Empty State Container (Belum Dibuat Mode) -->
    <div
      v-else
      class="border-2 border-dashed border-[#E5E5E5] rounded-2xl p-8 text-center my-[1rem] flex flex-col gap-[1rem] items-center justify-center"
    >
      <span class="k-bill text-[24px]" />
      <p class="text-[#525252] max-w-[300px] text-center mb-0 leading-relaxed font-medium">
        Belum ada Invoice. Silakan buat invoice prorata untuk melanjutkan proses.
      </p>
      <BButton
        variant="primary"
        class="bg-[#FF9B71] border-0 text-white font-medium px-5 text-sm shadow-sm"
        :disabled="data.status.code !== 'IN_PROGRESS'"
        @click="$emit('open-invoice', INVOICE_ACTION.MAKE_INV)"
      >
        Buat Invoice
      </BButton>
    </div>

    <!-- Footer Total -->
    <div class="pt-[1rem] border-t border-[#F0F0F0] flex items-start justify-between">
      <div
        class="w-full flex justify-between"
        :class="{ 'flex-col': hasInvoice }"
      >
        <p class="text-[#737373] mb-[.5rem]">
          Total Kewajiban:
        </p>
        <p class="text-xl font-semibold text-[#EC5A24] mb-0">
          {{ totalKewajibanFormatted }}
        </p>
      </div>
      <BButton
        v-if="hasInvoice && String(data.invoice.status).toUpperCase() !== 'PAID'"
        variant="danger"
        @click="openCancelModal"
      >
        Batalkan
      </BButton>
    </div>

    <!-- Modal Confirm Cancel Invoice -->
    <BModal
      id="modal-cancel-invoice"
      ref="bvModal"
      no-close-on-esc
      no-close-on-backdrop
      hide-header-close
      hide-footer
      hide-header
      :centered="true"
      content-class="border-0 rounded-[1.25rem]"
    >
      <div class="text-center p-[1rem] flex flex-col gap-[1rem]">
        <h3 class="font-medium text-[18px] text-[#000000] mb-0">
          Batalkan Invoice?
        </h3>
        <p class="text-[#737373] mb-[.5rem] leading-relaxed">
          Setelah proses dikonfirmasi, invoice harus diajukan ulang
        </p>
        <div class="flex items-center justify-between gap-[.75rem] mt-[.5rem]">
          <BButton
            variant="light"
            class="bg-[#F5F5F5] border-0 text-[#222222] font-semibold py-[.625rem] rounded-[10px] w-full hover:bg-[#EBEBEB] m-0"
            @click="closeCancelModal"
          >
            Batalkan
          </BButton>
          <BButton
            variant="primary"
            class="bg-[#F95031] border-0 text-white font-semibold py-[.625rem] rounded-[10px] w-full hover:bg-[#EA4323] m-0"
            :disabled="isLoadingCancel"
            @click="handleConfirmCancel"
          >
            <BSpinner
              v-if="isLoadingCancel"
              small
              class="mr-[.25rem]"
            />
            Konfirmasi
          </BButton>
        </div>
      </div>
    </BModal>
  </div>
</template>

<style scoped>
.btn-primary,
.btn-light,
.btn-danger {
  border-radius: 10px !important;
}
/* ── Status Invoice badges ──────────────── */
.badge-status-invoice {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  white-space: nowrap;
}

.badge-invoice--belum {
  background: #F5F5F5;
  color: #737373;
}

.badge-invoice--lunas {
  background: #D6F1DF;
  color: #30A46C;
}

.badge-invoice--unpaid {
  background: #FEEBEC;
  color: #E5484D;
}
</style>

<script>
import { komtimAxiosIns } from '@/libs/axios'
import { IDR } from '@/libs/currency'
import { RANGE_FROM_TODAY_WITH_DAYS } from '@/libs/filterDate'
import {
  INVOICE_ACTION, INVOICE_MAP, showSuccess, showError,
} from '../../config'

export default {
  name: 'InvoiceInfoCard',
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      INVOICE_ACTION,
      isLoadingCancel: false,
    }
  },
  computed: {
    hasInvoice() {
      return (this.data && this.data.invoice.detail && this.data.invoice.status !== 'NOT_YET_CREATED')
    },
    dueDateFormatted() {
      const dueDate = this.data && this.data.invoice?.detail?.due_date
      if (!dueDate) return '-'
      return RANGE_FROM_TODAY_WITH_DAYS(dueDate)
    },
    talentName() {
      return (this.data && this.data.invoice?.detail?.talent_name) || '-'
    },
    periode() {
      return (this.data && this.data.invoice?.detail?.period) || '-'
    },
    adminFeeFormated() {
      if (!this.data || this.data.invoice?.detail?.admin_fee == null) return 'Rp 0'
      return IDR(this.data.invoice.detail.admin_fee)
    },
    talentSalaryFormatted() {
      if (!this.data || this.data.invoice?.detail?.talent_salary == null) return 'Rp 0'
      return IDR(this.data.invoice.detail.talent_salary)
    },
    additionalCostFormatted() {
      if (!this.data || this.data.invoice?.detail?.additional_cost == null) return 'Rp 0'
      return IDR(this.data.invoice.detail.additional_cost)
    },
    totalKewajibanFormatted() {
      if (this.data && this.hasInvoice && this.data.invoice?.detail) {
        const { admin_fee: adminFee = 0, talent_salary: talentSalary = 0, additional_cost: additionalCost = 0 } = this.data.invoice.detail
        return IDR((adminFee || 0) + (talentSalary || 0) + (additionalCost || 0))
      }
      return 'Rp 0'
    },
  },
  methods: {
    getInvoiceLabel(status) {
      const s = String(status || '').toUpperCase()
      return (INVOICE_MAP[s] && INVOICE_MAP[s].label) || status
    },
    getInvoiceBadgeClass(status) {
      const s = String(status || '').toUpperCase()
      return (INVOICE_MAP[s] && INVOICE_MAP[s].style) || 'badge-invoice--belum'
    },
    openCancelModal() {
      this.$bvModal.show('modal-cancel-invoice')
    },
    closeCancelModal() {
      this.$bvModal.hide('modal-cancel-invoice')
    },
    async handleConfirmCancel() {
      const invoiceId = (this.data && this.data.invoice?.detail?.id) || null
      if (!invoiceId) return

      try {
        this.isLoadingCancel = true
        await komtimAxiosIns.delete(`/v1/invoices/${invoiceId}/delete`)
        showSuccess({ title: 'Berhasil Membatalkan Invoice!' })
        this.$emit('on-confirm')
      } catch {
        showError({ title: 'Gagal Membatalkan Invoice!' })
      } finally {
        this.closeCancelModal()
        setTimeout(() => {
          this.isLoadingCancel = false
        }, 800)
      }
    },
  },
}
</script>
